import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api, { endpoints } from '../../services/api';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Chip,
  Paper,
  Divider,
  InputAdornment,
  IconButton,
  Grid,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@mui/material';
import {
  Search as SearchIcon,
  Timeline as TimelineIcon,
  FlightTakeoff as FlightIcon,
  LocalShipping as ShippingIcon,
  Inventory as InventoryIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Schedule as ScheduleIcon,
  Assignment as ManifestIcon,
  Receipt as InvoiceIcon,
  ArrowBack as BackIcon,
  Refresh as RefreshIcon,
  ContentCopy as CopyIcon,
  QrCodeScanner as ScanIcon,
} from '@mui/icons-material';

// Formatear AWB o referencia WH para mostrar
const formatAwb = (awb) => {
  if (!awb) return '-';
  
  // Si es referencia WH, mostrar tal cual
  if (awb.toUpperCase().startsWith('WH')) {
    return awb.toUpperCase();
  }
  
  // Formatear como AWB
  const clean = awb.replace(/\D/g, '');
  if (clean.length === 11) {
    return `${clean.slice(0, 3)}-${clean.slice(3)}`;
  }
  return awb;
};

// Mapeo de iconos por tipo de evento
const getEventIcon = (eventType) => {
  const icons = {
    booked: <ScheduleIcon />,
    received: <InventoryIcon />,
    loaded: <ShippingIcon />,
    palletized: <ShippingIcon />,
    manifested: <ManifestIcon />,
    flown: <FlightIcon />,
    invoiced: <InvoiceIcon />,
    cancelled: <CancelIcon />,
    offloaded: <ShippingIcon />,
  };
  return icons[eventType] || <TimelineIcon />;
};

// Colores por tipo de evento
const getEventColor = (eventType) => {
  const colors = {
    booked: 'info',
    received: 'success',
    loaded: 'primary',
    palletized: 'secondary',
    manifested: 'warning',
    flown: 'success',
    invoiced: 'success',
    cancelled: 'error',
    offloaded: 'warning',
  };
  return colors[eventType] || 'default';
};

// Orden de los estados para el stepper
const statusOrder = ['booked', 'received', 'loaded', 'palletized', 'manifested', 'flown', 'invoiced'];

const AwbTracking = () => {
  const [searchParams] = useSearchParams();
  const [awb, setAwb] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [trackingData, setTrackingData] = useState(null);

  // Cargar AWB de URL si viene como parámetro
  useEffect(() => {
    const awbParam = searchParams.get('awb');
    if (awbParam) {
      const formatted = formatAwb(awbParam);
      setAwb(formatted);
      // Buscar automáticamente
      searchAwb(awbParam);
    }
  }, [searchParams]);

  // Formatear AWB mientras escribe (también acepta referencias WH-XXXX)
  const handleAwbChange = (e) => {
    let value = e.target.value;
    
    // Si empieza con WH, permitir el formato WH-XXXX
    if (value.toUpperCase().startsWith('WH')) {
      setAwb(value.toUpperCase());
      return;
    }
    
    // Si no, formatear como AWB
    value = value.replace(/\D/g, '');
    if (value.length > 11) value = value.slice(0, 11);
    
    // Formato: 123-45678900
    if (value.length > 3) {
      value = value.slice(0, 3) + '-' + value.slice(3);
    }
    setAwb(value);
  };

  // Función de búsqueda reutilizable (acepta AWB o referencia WH)
  const searchAwb = async (awbToSearch) => {
    const searchValue = (awbToSearch || awb).trim();
    
    // Verificar si es una referencia WH
    const isWhReference = searchValue.toUpperCase().startsWith('WH');
    
    if (isWhReference) {
      // Buscar por referencia WH
      const cleanRef = searchValue.toUpperCase();
      if (cleanRef.length < 3) {
        setError('Ingrese una referencia WH válida (ej: WH-4501)');
        return;
      }
      
      setError('');
      setLoading(true);
      setTrackingData(null);

      try {
        const res = await api.get(`${endpoints.tracking.status}?awb=${encodeURIComponent(cleanRef)}`);
        
        if (res.data.success && res.data.data) {
          setTrackingData(res.data.data);
        } else {
          setError('No se encontró información de tracking para esta referencia');
        }
      } catch (err) {
        console.error('Error:', err);
        if (err.response?.status === 404) {
          setError('No se encontró información de tracking para esta referencia');
        } else {
          setError(err.response?.data?.message || 'Error al consultar tracking');
        }
      } finally {
        setLoading(false);
      }
    } else {
      // Buscar por AWB tradicional
      const cleanAwb = searchValue.replace(/\D/g, '');
      
      if (!cleanAwb || cleanAwb.length < 11) {
        setError('Ingrese un AWB válido (11 dígitos) o referencia WH (ej: WH-4501)');
        return;
      }

      setError('');
      setLoading(true);
      setTrackingData(null);

      try {
        const res = await api.get(`${endpoints.tracking.status}?awb=${cleanAwb}`);
        
        if (res.data.success && res.data.data) {
          setTrackingData(res.data.data);
        } else {
          setError('No se encontró información de tracking para este AWB');
        }
      } catch (err) {
        console.error('Error:', err);
        if (err.response?.status === 404) {
          setError('No se encontró información de tracking para este AWB');
        } else {
          setError(err.response?.data?.message || 'Error al consultar tracking');
        }
      } finally {
        setLoading(false);
      }
    }
  };

  // Handler para el botón de búsqueda
  const handleSearch = () => {
    searchAwb(awb);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  // Calcular paso activo en el stepper
  const getActiveStep = () => {
    if (!trackingData) return -1;
    const currentStatus = trackingData.current_status;
    
    if (currentStatus === 'cancelled') return -1;
    
    const index = statusOrder.indexOf(currentStatus);
    return index >= 0 ? index : 0;
  };

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto' }}>
      {/* Header */}
      <Card
        sx={{
          mb: 3,
          background: 'linear-gradient(135deg, #0D47A1 0%, #1976D2 100%)',
          color: 'white',
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <TimelineIcon sx={{ fontSize: 48 }} />
            <Box>
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                Tracking de AWB
              </Typography>
              <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
                Consulta el estado y ubicación de tus guías aéreas
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Buscador */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Buscar Guía
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            <TextField
              fullWidth
              label="AWB o Referencia WH"
              placeholder="123-45678900 o WH-4501"
              value={awb}
              onChange={handleAwbChange}
              onKeyPress={handleKeyPress}
              helperText="Ingrese AWB (11 dígitos) o referencia WH (ej: WH-4501)"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <ScanIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="contained"
              size="large"
              onClick={handleSearch}
              disabled={loading || (!awb.toUpperCase().startsWith('WH') && awb.replace(/\D/g, '').length < 11) || (awb.toUpperCase().startsWith('WH') && awb.length < 4)}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
              sx={{ minWidth: 140, height: 56 }}
            >
              {loading ? 'Buscando...' : 'Buscar'}
            </Button>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Resultados */}
      {trackingData && (
        <>
          {/* Estado Actual */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                <Box>
                  <Typography variant="overline" color="text.secondary">
                    AWB
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h4" fontWeight="bold">
                      {formatAwb(trackingData.awb)}
                    </Typography>
                    <IconButton 
                      size="small" 
                      onClick={() => copyToClipboard(trackingData.awb)}
                      title="Copiar AWB"
                    >
                      <CopyIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="overline" color="text.secondary">
                    Estado Actual
                  </Typography>
                  <Box>
                    <Chip
                      icon={getEventIcon(trackingData.current_status)}
                      label={trackingData.current_status_name}
                      color={getEventColor(trackingData.current_status)}
                      size="large"
                      sx={{ fontSize: '1rem', py: 2.5 }}
                    />
                  </Box>
                </Box>
              </Box>

              {/* Info adicional */}
              <Grid container spacing={2}>
                {trackingData.current_flight && (
                  <Grid item xs={12} sm={6}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FlightIcon color="primary" />
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Vuelo Asignado
                          </Typography>
                          <Typography variant="h6" fontWeight="bold">
                            {trackingData.current_flight}
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>
                )}
                <Grid item xs={12} sm={trackingData.current_flight ? 6 : 12}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ScheduleIcon color="action" />
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Última Actualización
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {new Date(trackingData.last_update).toLocaleString('es-ES', {
                            dateStyle: 'medium',
                            timeStyle: 'short'
                          })}
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Progreso Visual */}
          {trackingData.current_status !== 'cancelled' && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Progreso de la Guía
                </Typography>
                <Stepper activeStep={getActiveStep()} alternativeLabel sx={{ mt: 2 }}>
                  {statusOrder.map((status, index) => {
                    const statusNames = {
                      booked: 'Reservado',
                      received: 'Recibido',
                      loaded: 'En Loading',
                      palletized: 'Paletizado',
                      manifested: 'Manifestado',
                      flown: 'Volado',
                      invoiced: 'Facturado',
                    };
                    const hasEvent = trackingData.events?.some(e => e.event_type === status);
                    
                    return (
                      <Step key={status} completed={hasEvent}>
                        <StepLabel
                          StepIconComponent={() => (
                            <Box
                              sx={{
                                width: 36,
                                height: 36,
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: hasEvent ? `${getEventColor(status)}.main` : 'grey.300',
                                color: hasEvent ? 'white' : 'grey.600',
                              }}
                            >
                              {hasEvent ? <CheckIcon fontSize="small" /> : getEventIcon(status)}
                            </Box>
                          )}
                        >
                          {statusNames[status]}
                        </StepLabel>
                      </Step>
                    );
                  })}
                </Stepper>
              </CardContent>
            </Card>
          )}

          {/* Historial de Eventos */}
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight="bold">
                  Historial de Eventos
                </Typography>
                <Chip 
                  label={`${trackingData.total_events} eventos`} 
                  size="small" 
                  variant="outlined"
                />
              </Box>
              
              <Stepper orientation="vertical">
                {[...(trackingData.events || [])].reverse().map((event, index) => (
                  <Step key={event.id || index} active expanded>
                    <StepLabel
                      StepIconComponent={() => (
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: `${getEventColor(event.event_type)}.main`,
                            color: 'white',
                          }}
                        >
                          {getEventIcon(event.event_type)}
                        </Box>
                      )}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography fontWeight="bold">
                          {event.event_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(event.created_at).toLocaleString('es-ES', {
                            dateStyle: 'short',
                            timeStyle: 'short'
                          })}
                        </Typography>
                      </Box>
                    </StepLabel>
                    <StepContent>
                      <Box sx={{ pl: 1 }}>
                        {event.flight_awb && (
                          <Typography variant="body2" color="text.secondary">
                            <FlightIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
                            Vuelo: {event.flight_awb}
                          </Typography>
                        )}
                        {event.created_by?.nombre && (
                          <Typography variant="body2" color="text.secondary">
                            Por: {event.created_by.nombre}
                            {event.created_by.cargo && ` (${event.created_by.cargo})`}
                          </Typography>
                        )}
                        {event.event_data && (
                          <Paper variant="outlined" sx={{ mt: 1, p: 1.5, bgcolor: 'grey.50' }}>
                            <Table size="small">
                              <TableBody>
                                {Object.entries(event.event_data).map(([key, value]) => (
                                  <TableRow key={key}>
                                    <TableCell sx={{ border: 0, py: 0.5, pl: 0, fontWeight: 'medium' }}>
                                      {key.replace(/_/g, ' ')}
                                    </TableCell>
                                    <TableCell sx={{ border: 0, py: 0.5 }}>
                                      {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </Paper>
                        )}
                      </Box>
                    </StepContent>
                  </Step>
                ))}
              </Stepper>

              {(!trackingData.events || trackingData.events.length === 0) && (
                <Alert severity="info">
                  No hay eventos registrados para esta guía
                </Alert>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Sin resultados inicial */}
      {!trackingData && !loading && !error && (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <TimelineIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Ingresa un número de AWB para consultar su tracking
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Podrás ver el estado actual, historial de movimientos y vuelo asignado
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Botón volver */}
      <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
        <Button
          component={Link}
          to="/warehouse"
          startIcon={<BackIcon />}
          variant="outlined"
        >
          Volver a Warehouse
        </Button>
        {trackingData && (
          <Button
            onClick={() => { setTrackingData(null); setAwb(''); }}
            startIcon={<RefreshIcon />}
            variant="outlined"
            color="secondary"
          >
            Nueva Búsqueda
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default AwbTracking;

